import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Alert, Badge, Box, ScopedCssBaseline, styled, IconButton, Drawer, useMediaQuery, Fab, Typography, keyframes, Snackbar } from '@mui/material'
import { Chat as ChatIcon, Close as CloseIcon, Videocam as VideocamIcon, CloseFullscreen as CollapseIcon, ScreenRotation as RotateIcon } from '@mui/icons-material'
import MasterAppBar from '../master/components/MasterAppBar'
import { useAuthContext } from '../auth/hooks/useAuthContext';
import DrawingBoard from './components/DrawingBoard';
import Tabs from './components/Tabs';
import StudentBoard from './components/StudentBoard';
import VideoStream from './components/VideoStream';
import ChatArea from './components/ChatArea';
import { socket } from '../../config/SocketApi';
import { recordingSocket } from '../../config/SocketApi';
import { useParams } from 'react-router-dom'
import StudentVideoStream from './components/StudentVideoStream';
import StudentPacingNotice from './components/StudentPacingNotice';
import ShareScreenHelper from './helpers/shareScreenHelper';
import SelectSreenShare from './components/SelectSreenShare';
import ButtomBar from './components/ButtomBar';
import { useNavigate } from 'react-router-dom';
import SelectMeeting from './components/SelectMeeting';
import { reset } from '../../features/tabs/tabsSlice';
import { toggleMeetingState } from '../../features/meeting/meetingSlice';
import { useDispatch } from 'react-redux';
import ActionCenter from '../shared/ActionCenter';
import StudentPoll from './components/StudentPoll';
import { useTranslation } from 'react-i18next';
import API from '../../config/API';
import StudentScreenShareControl from './components/StudentScreenShareControl';

// Memoized without a custom comparator so orientation/layout changes still flow through.
const MemoizedStudentBoard = React.memo(StudentBoard);
const MemoizedStudentVideoStream = React.memo(StudentVideoStream);
const MemoizedChatArea = React.memo(ChatArea);

// MOBILE BREAKPOINT: 1000px - anything wider shows desktop/laptop mode
const MOBILE_BREAKPOINT = 1000;

// Rotate animation
const rotateAnimation = keyframes`
    0% { transform: rotate(0deg); }
    25% { transform: rotate(-15deg); }
    50% { transform: rotate(0deg); }
    75% { transform: rotate(15deg); }
    100% { transform: rotate(0deg); }
`;

const pulseAnimation = keyframes`
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(0.95); }
`;

// STYLED COMPONENTS
const WhiteBoardContainer = styled(Box)(({ theme }) => ({
    height: '100vh',
    width: '100%',
    maxWidth: '100vw',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#0f0f1a',
    background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
}));

const ClassRoomBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    flex: 1,
    width: '100%',
    gap: '8px',
    padding: '8px',
    minHeight: 0,
    boxSizing: 'border-box',
}));

const RightBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    borderRadius: '12px',
    backgroundColor: 'rgba(26, 26, 46, 0.6)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const ButtonsBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
    height: '56px',
    flexShrink: 0,
    padding: '0 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const LeftBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    width: '300px',
    minWidth: '300px',
    maxWidth: '300px',
    flexDirection: 'column',
    flexShrink: 0,
    gap: '8px',
    minHeight: 0,
}));

// Rotate Phone Overlay for Portrait Mobile
const RotateOverlay = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 15, 26, 0.98)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '24px',
    textAlign: 'center',
}));

const PhoneIcon = styled(Box)(({ theme }) => ({
    width: '80px',
    height: '140px',
    border: '4px solid #667eea',
    borderRadius: '16px',
    position: 'relative',
    marginBottom: '32px',
    animation: `${rotateAnimation} 2s ease-in-out infinite`,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '8px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '20px',
        height: '4px',
        backgroundColor: '#667eea',
        borderRadius: '2px',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '8px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '24px',
        height: '24px',
        border: '3px solid #667eea',
        borderRadius: '50%',
    },
}));

const WhitBoard = () => {
    const dispatch = useDispatch()
    const { user } = useAuthContext() || JSON.parse(localStorage.getItem("user"));
    const { s_id, tu_id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    
    // Mobile detection - Changed breakpoint to 1000px
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    // Portrait detection for mobile
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const showRotateOverlay = isMobile && isPortrait;
    
    // Mobile UI state
    const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [messageToast, setMessageToast] = useState(null);
    const [showMobileVideo, setShowMobileVideo] = useState(true);
    const [studentMicRequestOpen, setStudentMicRequestOpen] = useState(false);
    const isDraggingRef = useRef(false);
    const dragMovedRef = useRef(false);
    const dragStartRef = useRef({ x: 0, y: 0, pointerX: 0, pointerY: 0 });
    const [mobileVideoPosition, setMobileVideoPosition] = useState({ x: 16, y: 100 });
    const mobileVideoSize = showMobileVideo ? 100 : 48;
    const endSessionInProgressRef = useRef(false);

    const finalizeSessionLocally = useCallback(() => {
        socket.emit("stop-room-recordings", { room_id: s_id });
        socket.emit("end-browser-page", { room_id: s_id });
        navigate('/');
    }, [s_id, navigate]);

    const handleEndSession = useCallback(async () => {
        if (endSessionInProgressRef.current) {
            return true;
        }

        endSessionInProgressRef.current = true;
        try {
            const response = await API.post(`session/status`, {
                session_id: s_id,
                new_status: 2,
            }, {
                headers: {
                    Authorization: user?.token,
                    "Content-Type": 'application/json'
                }
            });

            const ok = response?.status >= 200 && response?.status < 300 && response?.data?.status !== 'error';
            if (!ok) {
                throw new Error(response?.data?.message || 'Unable to update session status');
            }

            finalizeSessionLocally();
            return true;
        } catch (error) {
            console.log("[Classroom] Failed to end session:", error?.message || error);
            endSessionInProgressRef.current = false;
            return false;
        }
    }, [s_id, user?.token, finalizeSessionLocally]);
    
    // Mobile draggable video handlers
    const videoContainerRef = useRef(null);
    
    const handleDragStart = useCallback((e) => {
        if (!isMobile) return;
        if (!e.currentTarget.contains(e.target)) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        isDraggingRef.current = true;
        dragMovedRef.current = false;
        dragStartRef.current = {
            x: e.clientX - mobileVideoPosition.x,
            y: e.clientY - mobileVideoPosition.y,
            pointerX: e.clientX,
            pointerY: e.clientY,
        };
    }, [isMobile, mobileVideoPosition]);

    const handleDragMove = useCallback((e) => {
        if (!isDraggingRef.current) return;
        if (!e.currentTarget.contains(e.target)) return;
        e.preventDefault();
        const viewportWidth = window.visualViewport?.width || window.innerWidth;
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const distance = Math.hypot(
            e.clientX - dragStartRef.current.pointerX,
            e.clientY - dragStartRef.current.pointerY,
        );
        if (distance > 4) dragMovedRef.current = true;
        setMobileVideoPosition({
            x: Math.max(0, Math.min(viewportWidth - mobileVideoSize, e.clientX - dragStartRef.current.x)),
            y: Math.max(0, Math.min(viewportHeight - mobileVideoSize, e.clientY - dragStartRef.current.y)),
        });
    }, [mobileVideoSize]);

    const handleDragEnd = useCallback((e) => {
        if (!isDraggingRef.current) return;
        if (!e.currentTarget.contains(e.target)) return;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        isDraggingRef.current = false;
        if (!dragMovedRef.current) {
            setShowMobileVideo((visible) => !visible);
        }
    }, []);

    useEffect(() => {
        if (!isMobile) return undefined;
        const constrainPosition = () => {
            const viewportWidth = window.visualViewport?.width || window.innerWidth;
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            setMobileVideoPosition((position) => ({
                x: Math.max(0, Math.min(viewportWidth - mobileVideoSize, position.x)),
                y: Math.max(0, Math.min(viewportHeight - mobileVideoSize, position.y)),
            }));
        };
        constrainPosition();
        window.addEventListener('resize', constrainPosition);
        window.visualViewport?.addEventListener('resize', constrainPosition);
        return () => {
            window.removeEventListener('resize', constrainPosition);
            window.visualViewport?.removeEventListener('resize', constrainPosition);
        };
    }, [isMobile, mobileVideoSize]);
    
    useEffect(() => {
        if (!user) return
        if (user.user_type !== 0 && parseInt(tu_id) !== user.id) {
            socket.auth = { token: user?.token, room_id: s_id, user_type: 0, user_id: user?.id, user_name: user?.full_name, user_image: user?.img_path, isRecording: false }
            socket.connect()
            recordingSocket.auth = { token: user?.token, room_id: s_id, user_type: 0, user_id: user?.id, user_name: user?.full_name, user_image: user?.img_path, isRecording: true }
            recordingSocket.connect()
            recordingSocket.on("end-session", () => {
                if (user?.user_type !== 0) handleEndSession()
            })
        } else {
            socket.auth = { token: user?.token, room_id: s_id, user_type: user.user_type, user_id: user?.id, user_name: user?.full_name, user_image: user?.img_path, isRecording: false }
            socket.connect()
            recordingSocket.auth = { token: user?.token, room_id: s_id, user_type: user.user_type, user_id: user?.id, user_name: user?.full_name, user_image: user?.img_path, isRecording: true }
            recordingSocket.connect()
            recordingSocket.on("end-session", () => {
                if (user?.user_type !== 0) handleEndSession()
            })
        }
        return () => {
            dispatch(reset({}))
            dispatch(toggleMeetingState({ status: false }))
            recordingSocket.off("end-session");
            if (socket.connected) socket.disconnect();
            if (recordingSocket.connected) recordingSocket.disconnect()
        }

    }, [user, s_id, tu_id, dispatch, handleEndSession])

    // User type detection
    const userType = user ? Number(user.user_type) : -1;
    const tutorIdFromUrl = parseInt(tu_id);
    const currentUserId = parseInt(user?.id);
    const isTeacher = userType === 1 && tutorIdFromUrl === currentUserId;
    const isStudent = !isTeacher;

    const handleOpenChat = useCallback(() => {
        setChatDrawerOpen(true);
        setUnreadMessages(0);
    }, []);

    const handleIncomingMessage = useCallback((incoming) => {
        if (isMobile && !chatDrawerOpen) {
            setUnreadMessages((count) => Math.min(count + 1, 99));
        }
        setMessageToast({
            sender: incoming?.fullName || incoming?.userName || t('chatbox') || 'Chat',
            message: incoming?.message || '',
        });
    }, [chatDrawerOpen, isMobile, t]);

    useEffect(() => {
        if (!isStudent) return undefined;

        const handleOpenMicRequest = () => {
            console.log('[Classroom] Received student microphone request');
            setStudentMicRequestOpen(true);
        };

        socket.on('open-mic', handleOpenMicRequest);
        return () => socket.off('open-mic', handleOpenMicRequest);
    }, [isStudent, s_id]);

    const mobileRotateOverlay = showRotateOverlay ? (
        <RotateOverlay>
            <PhoneIcon />
            <Typography
                variant="h5"
                sx={{
                    color: '#fff',
                    fontWeight: 700,
                    mb: 2,
                    animation: `${pulseAnimation} 2s ease-in-out infinite`,
                }}
            >
                {t('rotatePhone') || 'Please Rotate Your Phone'}
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: 'rgba(255,255,255,0.7)',
                    maxWidth: 280,
                    lineHeight: 1.6,
                }}
            >
                {t('rotatePhoneMessage') || 'For the best classroom experience, please rotate your device to landscape mode.'}
            </Typography>
            <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                <RotateIcon sx={{ color: '#667eea', fontSize: 28 }} />
                <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
                    {t('landscape') || 'Landscape Mode'}
                </Typography>
            </Box>
        </RotateOverlay>
    ) : null;

    // Mobile student layout with rotate overlay
    if (isMobile && isStudent) {
        // Keep the classroom mounted while the portrait overlay is visible.
        // Unmounting live peers during rotation drops the whiteboard stream.
        return (
            <ScopedCssBaseline>
                <ActionCenter />
                {user && <WhiteBoardContainer>
                    {/* Full-screen whiteboard - NO header for full immersion */}
                    <Box sx={{
                        flex: 1,
                        position: 'relative',
                        isolation: 'isolate',
                        overflow: 'hidden',
                        width: '100%',
                        height: '100%',
                        minWidth: 0,
                    }} data-classroom-stage="true" data-testid="classroom-stage">
                        <MemoizedStudentBoard />
                        
                        {/* Floating chat FAB */}
                        <Fab
                            color="primary"
                            size="medium"
                            aria-label="Open chat"
                            onClick={handleOpenChat}
                            sx={{
                                position: 'fixed',
                                bottom: 20,
                                right: 16,
                                bgcolor: 'rgba(102, 126, 234, 0.9)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: 'rgba(102, 126, 234, 1)' },
                                zIndex: 10,
                            }}
                        >
                            <Badge badgeContent={unreadMessages} color="error" max={99}>
                                <ChatIcon />
                            </Badge>
                        </Fab>

                        <StudentScreenShareControl roomId={s_id} direction="ltr" mobile />
                        
                        {/* Draggable circular video overlay */}
                        <Box
                            ref={videoContainerRef}
                            data-testid="mobile-camera-bubble"
                            role="button"
                            tabIndex={0}
                            aria-label={showMobileVideo ? 'Collapse teacher camera' : 'Open teacher camera'}
                            onPointerDown={handleDragStart}
                            onPointerMove={handleDragMove}
                            onPointerUp={handleDragEnd}
                            onPointerCancel={() => {
                                isDraggingRef.current = false;
                                dragMovedRef.current = true;
                            }}
                            onKeyDown={(event) => {
                                if (!event.currentTarget.contains(event.target)) return;
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    setShowMobileVideo((visible) => !visible);
                                }
                            }}
                            sx={{
                                position: 'absolute',
                                left: mobileVideoPosition.x,
                                top: mobileVideoPosition.y,
                                width: mobileVideoSize,
                                height: mobileVideoSize,
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: '3px solid rgba(102, 126, 234, 0.8)',
                                bgcolor: 'rgba(15, 15, 26, 0.94)',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
                                zIndex: 10,
                                cursor: 'grab',
                                transition: 'width 0.2s ease, height 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    boxShadow: '0 6px 25px rgba(102, 126, 234, 0.5)',
                                },
                                touchAction: 'none',
                            }}
                        >
                            <Box sx={{ width: '100%', height: '100%', opacity: showMobileVideo ? 1 : 0, pointerEvents: 'none' }}>
                                <MemoizedStudentVideoStream
                                    isMobileOverlay={true}
                                    micRequestOpen={studentMicRequestOpen}
                                    onMicRequestHandled={() => setStudentMicRequestOpen(false)}
                                />
                            </Box>
                            {!showMobileVideo && (
                                <VideocamIcon sx={{ position: 'absolute', inset: 0, m: 'auto', color: '#fff', fontSize: 24 }} />
                            )}
                            {showMobileVideo && (
                                <Box sx={{
                                    position: 'absolute',
                                    right: 5,
                                    bottom: 5,
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    display: 'grid',
                                    placeItems: 'center',
                                    bgcolor: 'rgba(15, 15, 26, 0.72)',
                                    color: '#fff',
                                    pointerEvents: 'none',
                                }}>
                                    <CollapseIcon sx={{ fontSize: 15 }} />
                                </Box>
                            )}
                        </Box>
                        
                        {/* Chat Drawer */}
                        <Drawer
                            anchor="right"
                            open={chatDrawerOpen}
                            onClose={() => setChatDrawerOpen(false)}
                            ModalProps={{ keepMounted: true }}
                             PaperProps={{
                                 sx: {
                                     width: '85vw',
                                     maxWidth: 350,
                                     height: '100dvh',
                                     display: 'flex',
                                     flexDirection: 'column',
                                     bgcolor: 'rgba(26, 26, 46, 0.98)',
                                    backdropFilter: 'blur(20px)',
                                }
                            }}
                        >
                            <IconButton 
                                aria-label="Close chat"
                                onClick={() => setChatDrawerOpen(false)} 
                                sx={{ 
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    color: '#fff',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    zIndex: 10,
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                    }
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', pt: 1, display: 'flex', flexDirection: 'column' }}>
                                <StudentPacingNotice roomId={s_id} />
                                {s_id && (
                                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                        <MemoizedChatArea
                                            room_id={s_id}
                                            onIncomingMessage={handleIncomingMessage}
                                            allowStudentScreenShare={false}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Drawer>
                    </Box>

                    <StudentPoll />
                </WhiteBoardContainer>}
                {mobileRotateOverlay}
                <Snackbar
                    open={Boolean(messageToast)}
                    autoHideDuration={4500}
                    onClose={() => setMessageToast(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity="info" onClose={() => setMessageToast(null)} sx={{ width: '100%' }}>
                        <strong>{messageToast?.sender}</strong>{messageToast?.message ? `: ${messageToast.message}` : ''}
                    </Alert>
                </Snackbar>
            </ScopedCssBaseline>
        );
    }

    // Desktop layout (original)
    return (
        <ScopedCssBaseline>
            <ActionCenter />
            {user && <WhiteBoardContainer>
                <MasterAppBar crumb={false} virtualRoom={true} />
                <ClassRoomBox>
                    <RightBox>
                        {/* ONLY render teacher UI if isTeacher is true */}
                        {isTeacher && (
                            <>
                                <ButtonsBox>
                                    <SelectSreenShare room_id={s_id} />
                                    <SelectMeeting />
                                </ButtonsBox>
                                <Box
                                    data-classroom-stage="true"
                                    data-testid="classroom-stage"
                                    sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, minWidth: 0, position: 'relative', isolation: 'isolate', overflow: 'hidden' }}
                                >
                                    <DrawingBoard />
                                    <Tabs />
                                    <ShareScreenHelper />
                                </Box>
                            </>
                        )}
                        {/* Student whiteboard - FILL AVAILABLE SPACE */}
                        {!isTeacher && (
                            <Box
                                data-classroom-stage="true"
                                data-testid="classroom-stage"
                                sx={{ flex: 1, minHeight: 0, minWidth: 0, position: 'relative', isolation: 'isolate', overflow: 'hidden' }}
                            >
                                <StudentBoard />
                            </Box>
                        )}
                    </RightBox>
                    <LeftBox>
                        {isTeacher ? <VideoStream /> : (
                            <StudentVideoStream
                                micRequestOpen={studentMicRequestOpen}
                                onMicRequestHandled={() => setStudentMicRequestOpen(false)}
                            />
                        )}
                        {isStudent && <StudentPacingNotice roomId={s_id} />}
                        {s_id && <MemoizedChatArea room_id={s_id} onIncomingMessage={isStudent ? handleIncomingMessage : undefined} />}
                    </LeftBox>
                </ClassRoomBox>
                {isTeacher && <Box data-testid="recording-controls" sx={{ flexShrink: 0 }}><ButtomBar room_id={s_id} onEndSession={handleEndSession} /></Box>}
                {!isTeacher && <StudentPoll />}
            </WhiteBoardContainer>}
            <Snackbar
                open={Boolean(messageToast)}
                autoHideDuration={4500}
                onClose={() => setMessageToast(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="info" onClose={() => setMessageToast(null)} sx={{ width: '100%' }}>
                    <strong>{messageToast?.sender}</strong>{messageToast?.message ? `: ${messageToast.message}` : ''}
                </Alert>
            </Snackbar>
        </ScopedCssBaseline>
    )
}

export default WhitBoard
